import React from 'react';
import PropTypes from 'prop-types';
import SVGIcon from '../SVGIcon/SVGIcon';
import { styles } from './TextInput.style';

/**
 * The TextInput component represents the standard html element `<input />` with adjusted styling.
 * This component gives the possibility to add extra elements as an icon or button to improve the functionality of the input element.
 */

const TextInput = React.forwardRef(
  (
    {
      className = '',
      validated = false,
      invalid = false,
      leftElement = null,
      rightElement = null,
      inputValue = '',
      onChange = () => {},
      placeholder = '',
      type = 'text',
      ...inputAttributes
    },
    ref
  ) => {
    const rightElementButtonChild =
      rightElement &&
      rightElement.type &&
      rightElement.type.name &&
      rightElement.type.name === 'Button';
    const showRightContentWrapper = rightElement || validated;
    return (
      <div tw="relative">
        {leftElement && <span css={styles.leftElement}>{leftElement}</span>}
        <input
          {...inputAttributes}
          type={type}
          onChange={onChange}
          ref={ref}
          placeholder={placeholder}
          value={inputValue}
          css={styles.defaultInput({ $invalid: invalid, leftElement, rightElement })}
          className={className}
        />
        {showRightContentWrapper && (
          <div css={styles.rightContentWrapper}>
            {validated && (
              <span css={styles.rightElement({ rightElementButtonChild })}>
                <SVGIcon icon="check" tw="text-green-darken-10" />
              </span>
            )}
            {rightElement && (
              <span css={styles.rightElement({ rightElementButtonChild })}>
                {rightElement}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

TextInput.propTypes = {
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string,
  /** Define the input type */
  type: PropTypes.string,
  /** Define the input value */
  value: PropTypes.string,
  /** Set the input to invalid state */
  invalid: PropTypes.bool,
  /** Set the input to validated state */
  validated: PropTypes.bool,
  /** Set the input to disabled state */
  disabled: PropTypes.bool,
  /** Fill in a placeholder text */
  placeholder: PropTypes.string,
  /** Add an element to the left of the input */
  leftElement: PropTypes.element,
  /** Add an element to the right of the input */
  rightElement: PropTypes.element,
  /** Input value passed on by wrapping container */
  inputValue: PropTypes.string,
  /** onChange event for input field */
  onChange: PropTypes.oneOfType([PropTypes.func, PropTypes.bool])
};

export default TextInput;
